// ---------------------------------------------------------------------------
// Event date range formatter (phase 36-02)
//
// Pure, SSR-safe helpers that format the date-line shown in the hero and
// related-card teasers on event pages. SSR-safety here means: NO
// `Date.toLocaleDateString()` without a fixed locale, NO `Intl.DateTimeFormat`
// with a locale arg that differs between node-icu and browser Intl. We
// instead map UTC month parts through a frozen `MONTH_ABBR` table so node
// and browser emit identical bytes and Angular's hydration diff stays quiet.
//
// Spec: `.guillaume/specs/strapi-to-frontend/24-event-page.md` lines 50-54.
// ---------------------------------------------------------------------------

const MONTH_ABBR: ReadonlyArray<string> = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_FULL: ReadonlyArray<string> = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** U+2013 EN DASH — separator between start and end dates per spec. */
const EN_DASH = '\u2013';

interface DateParts {
  y: number;
  m: number;   // 0-indexed month
  d: number;   // 1-indexed day of month
}

function parseIso(iso: string | null | undefined): DateParts | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const dt = new Date(t);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth(), d: dt.getUTCDate() };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * True when two JS `Date` values refer to the same calendar day in UTC.
 * Exposed primarily for unit testing and for callers that want to branch
 * on "same-day" independent of the range formatter.
 */
export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

/**
 * Format an ISO start/end date pair into the spec's display string:
 *
 *   - startDate only, or start/end same calendar day -> 'Mar 18, 2026'
 *   - start and end on different calendar days       -> 'Mar 18 \u2013 Mar 20, 2026'
 *   - start and end in different months              -> 'Mar 28 \u2013 Apr 02, 2026'
 *   - start and end in different years               -> 'Dec 30, 2026 \u2013 Jan 02, 2027'
 *   - start missing                                  -> '' (caller suppresses line)
 *
 * Uses a frozen month-abbreviation table so SSR (node-icu) and browser
 * (Intl) render identical bytes, avoiding Angular hydration mismatches.
 * Compares calendar days via UTC parts (spec dates are timezone-naive
 * conference dates, not timestamps).
 */
export function formatEventDateRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  const start = parseIso(startIso);
  if (!start) return '';

  const end = parseIso(endIso);

  // Single-day (no end or same-day as start)
  if (!end || (start.y === end.y && start.m === end.m && start.d === end.d)) {
    return `${MONTH_ABBR[start.m]} ${pad2(start.d)}, ${start.y}`;
  }

  // Different years -> full date on both sides
  if (start.y !== end.y) {
    return `${MONTH_ABBR[start.m]} ${pad2(start.d)}, ${start.y} ${EN_DASH} ${MONTH_ABBR[end.m]} ${pad2(end.d)}, ${end.y}`;
  }

  // Same year, different months -> month+day on left, month+day, year on right
  if (start.m !== end.m) {
    return `${MONTH_ABBR[start.m]} ${pad2(start.d)} ${EN_DASH} ${MONTH_ABBR[end.m]} ${pad2(end.d)}, ${end.y}`;
  }

  // Same month, different day -> MMM DD \u2013 MMM DD, YYYY (keep 'Mar 18 \u2013 Mar 20, 2026')
  return `${MONTH_ABBR[start.m]} ${pad2(start.d)} ${EN_DASH} ${MONTH_ABBR[end.m]} ${pad2(end.d)}, ${end.y}`;
}

/**
 * Format an ISO date string as "Month YYYY" (full month name, no day).
 * Returns '' on null/undefined/invalid input \u2014 caller suppresses the
 * meta line via [str, 'Webinar'].filter(Boolean).join(' \u00b7 ').
 *
 * SSR-safe: uses the same frozen-table pattern as MONTH_ABBR; never the
 * non-deterministic localised formatters that the file banner warns
 * against. Mirrors formatEventDateRange.
 *
 * Examples:
 *   formatRecordedDate('2024-04-15') -> 'April 2024'
 *   formatRecordedDate('2024-01-01T00:00:00Z') -> 'January 2024'
 *   formatRecordedDate(null) -> ''
 *   formatRecordedDate(undefined) -> ''
 *   formatRecordedDate('') -> ''
 *
 * Pitfall 26: NG0500 hydration mismatch \u2014 locale-sensitive formatters
 * emit different bytes between Chrome/Safari Intl and node-icu (separator
 * comma differs across ICU versions). Frozen table is byte-deterministic.
 *
 * Owned by 45-01 foundation \u2014 45-02 (metadata strip) and 45-03 (archive
 * card decorator) import this; neither modifies event-date.util.ts.
 */
export function formatRecordedDate(iso: string | null | undefined): string {
  const parts = parseIso(iso);
  if (!parts) return '';
  return `${MONTH_FULL[parts.m]} ${parts.y}`;
}
